import React, { useMemo, useState } from 'react';
import { Download, Eye, FileText, Image as ImageIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { getPropertyDocumentDownloadUrl } from '../../services/api';
import { getFileKindFromUrl } from '../../utils/fileKind';

type Props = {
  title: string;
  fileUrl: string;
  fileTypeLabel?: string;
  downloadName?: string;
  className?: string;
};

export const DocumentViewer: React.FC<Props> = ({
  title,
  fileUrl,
  fileTypeLabel,
  downloadName,
  className,
}) => {
  const [isImageOpen, setIsImageOpen] = useState(false);

  const href = useMemo(() => getPropertyDocumentDownloadUrl(fileUrl), [fileUrl]);
  const kind = useMemo(() => getFileKindFromUrl(href), [href]);

  const handleView = () => {
    if (kind === 'image') {
      setIsImageOpen(true);
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const icon =
    kind === 'image' ? (
      <ImageIcon className="w-6 h-6 text-primary" />
    ) : (
      <FileText className="w-6 h-6 text-primary" />
    );

  return (
    <>
      <div
        className={
          className ||
          'p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all'
        }
      >
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              {icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">{title}</h3>
            {fileTypeLabel && (
              <p className="text-xs text-gray-500 mt-1">{fileTypeLabel}</p>
            )}
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={handleView}
            className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <Eye className="w-4 h-4" />
            <span>{kind === 'image' ? 'Ver imagen' : kind === 'pdf' ? 'Ver PDF' : 'Ver'}</span>
          </button>

          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            download={downloadName}
            className="inline-flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Descargar</span>
          </a>
        </div>
      </div>

      {kind === 'image' && (
        <Modal
          isOpen={isImageOpen}
          onClose={() => setIsImageOpen(false)}
          title={title}
          size="lg"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 overflow-hidden bg-black/5">
              <img
                src={href}
                alt={title}
                className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              />
            </div>
            <div className="flex justify-end">
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                download={downloadName}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Descargar</span>
              </a>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

